import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CATEGORIES = ['academics','learning','hrm','finance','crm','operations','communication','reports','administration'];
const CAT_LABELS = {
  academics: 'Academics', learning: 'Learning', hrm: 'HR & Payroll',
  finance: 'Finance', crm: 'CRM', operations: 'Operations',
  communication: 'Communication', reports: 'Reports', administration: 'Administration',
};
const CAT_COLORS = {
  academics: '#6366f1', learning: '#8b5cf6', hrm: '#0891b2', finance: '#059669',
  crm: '#d97706', operations: '#dc2626', communication: '#7c3aed', reports: '#0369a1', administration: '#374151',
};
const LIMIT = 25;

export default function SAModules() {
  const [modules,    setModules]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [industries, setIndustries] = useState([]);
  const [plans,      setPlans]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState({ show: false, message: '', type: 'success' });

  // Filters
  const [filterCat,      setFilterCat]      = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [search,         setSearch]         = useState('');
  const [searchInput,    setSearchInput]    = useState('');
  const searchTimer = useRef(null);

  const blankForm = { name: '', icon: 'bi bi-grid', routePath: '', category: 'operations', minPlanTier: 'basic', isCore: false, order: 0, description: '', industryIds: [], hasCustomPage: false };
  const [form, setForm] = useState(blankForm);

  // Load reference data once
  useEffect(() => {
    Promise.all([
      api.get('/sa/industries').catch(() => ({ data: {} })),
      api.get('/sa/plans').catch(() => ({ data: {} })),
    ]).then(([i, p]) => {
      setIndustries(i.data?.records || i.data?.data || []);
      setPlans(p.data?.records || p.data?.data || []);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filterCat)      params.category   = filterCat;
      if (filterIndustry) params.industryId = filterIndustry;
      if (search)         params.search     = search;
      const r = await api.get('/sa/modules', { params });
      setModules(r.data?.records || []);
      setTotal(r.data?.total || 0);
      setTotalPages(r.data?.totalPages || 1);
    } catch {
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterCat, filterIndustry, search]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when filters change
  const applyFilter = (setter, value) => { setter(value); setPage(1); };

  // Debounced search
  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setSearch(val); setPage(1); }, 400);
  };

  const openEdit = (mod) => {
    setEditing(mod);
    setForm({ ...blankForm, ...mod, industryIds: (mod.industryIds || []).map(i => i._id || i) });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const openNew = () => { setEditing(null); setForm(blankForm); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/sa/modules/${editing._id}`, form);
      else         await api.post('/sa/modules', form);
      setToast({ show: true, message: `Module ${editing ? 'updated' : 'created'}!`, type: 'success' });
      setShowForm(false);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error saving module', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (mod) => {
    if (!window.confirm(`Delete "${mod.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sa/modules/${mod._id}`);
      setToast({ show: true, message: 'Module deleted', type: 'success' });
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Delete failed', type: 'danger' });
    }
  };

  const toggleIndustry = (id) => {
    setForm(prev => {
      const ids = prev.industryIds || [];
      return { ...prev, industryIds: ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id] };
    });
  };

  const tierColor = { basic: 'secondary', pro: 'primary', enterprise: 'warning' };

  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to   = Math.min(page * LIMIT, total);

  return (
    <div>
      <PageHeader
        title="Modules"
        icon="bi-grid-3x3-gap"
        subtitle={`${total} module${total !== 1 ? 's' : ''} across all industries`}
        actions={
          <button className="btn btn-primary btn-sm" onClick={openNew}>
            <i className="bi bi-plus-lg me-1"></i>Add Module
          </button>
        }
      />

      {/* ── Create / Edit Form ────────────────────────────────────────────── */}
      {showForm && (
        <div className="card mb-4" style={{ border: '2px solid #6366f1', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff' }}>
            <span className="fw-bold" style={{ color: '#4338ca' }}>
              <i className={`bi ${editing ? 'bi-pencil' : 'bi-plus-circle'} me-2`}></i>
              {editing ? `Edit: ${editing.name}` : 'New Module'}
            </span>
            <button className="btn-close" onClick={() => setShowForm(false)}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label">Module Name *</label>
                  <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Icon (Bootstrap Icons)</label>
                  <div className="input-group">
                    <span className="input-group-text"><i className={form.icon || 'bi bi-grid'}></i></span>
                    <input className="form-control" placeholder="bi bi-grid" value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Route Path *</label>
                  <input className="form-control" placeholder="/students" value={form.routePath} onChange={e => setForm(f => ({ ...f, routePath: e.target.value }))} required />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c] || c}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Min Plan Tier</label>
                  <select className="form-select" value={form.minPlanTier} onChange={e => setForm(f => ({ ...f, minPlanTier: e.target.value }))}>
                    {plans.length > 0
                      ? plans.map(p => <option key={p._id} value={p.tier}>{p.name} ({p.tier})</option>)
                      : ['basic','pro','enterprise'].map(t => <option key={t} value={t}>{t}</option>)
                    }
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Order</label>
                  <input type="number" className="form-control" value={form.order} onChange={e => setForm(f => ({ ...f, order: +e.target.value }))} />
                </div>
                <div className="col-md-3 d-flex align-items-end gap-3 pb-1">
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={!!form.isCore} onChange={e => setForm(f => ({ ...f, isCore: e.target.checked }))} />
                    <label className="form-check-label" style={{ fontSize: 13 }}>Core module</label>
                  </div>
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" checked={!!form.hasCustomPage} onChange={e => setForm(f => ({ ...f, hasCustomPage: e.target.checked }))} />
                    <label className="form-check-label" style={{ fontSize: 13 }}>Custom page</label>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Industries <span className="text-muted fw-normal" style={{ fontSize: 12 }}>(select all that apply)</span></label>
                  <div className="d-flex flex-wrap gap-2">
                    {industries.map(i => {
                      const active = (form.industryIds || []).includes(i._id);
                      return (
                        <button type="button" key={i._id}
                          className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ borderRadius: 20, fontSize: 12 }}
                          onClick={() => toggleIndustry(i._id)}>
                          {active && <i className="bi bi-check2 me-1"></i>}
                          {i.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}></textarea>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-light btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Module' : 'Create Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-body py-2 px-3">
          <div className="row g-2 align-items-center">
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search modules..."
                  value={searchInput}
                  onChange={e => handleSearchChange(e.target.value)}
                />
                {searchInput && (
                  <button className="btn btn-outline-secondary" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filterIndustry} onChange={e => applyFilter(setFilterIndustry, e.target.value)}>
                <option value="">All Industries</option>
                {industries.map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={filterCat} onChange={e => applyFilter(setFilterCat, e.target.value)}>
                <option value="">All Categories</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="col-md-2 text-end">
              <span className="text-muted" style={{ fontSize: 12 }}>
                {total > 0 ? `${from}–${to} of ${total}` : '0 results'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ width: 32 }}>#</th>
                    <th>Module</th>
                    <th>Key</th>
                    <th>Category</th>
                    <th>Industries</th>
                    <th>Tier</th>
                    <th style={{ width: 60 }}>Core</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-5 text-muted">
                      <i className="bi bi-grid" style={{ fontSize: 32, opacity: 0.3, display: 'block', marginBottom: 8 }}></i>
                      No modules found
                      {(search || filterCat || filterIndustry) && (
                        <div className="mt-2">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(''); setSearchInput(''); setFilterCat(''); setFilterIndustry(''); setPage(1); }}>
                            Clear filters
                          </button>
                        </div>
                      )}
                    </td></tr>
                  ) : modules.map((mod, idx) => (
                    <tr key={mod._id}>
                      <td className="text-muted" style={{ fontSize: 11 }}>{from + idx}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: 28, height: 28, borderRadius: 6, background: `${CAT_COLORS[mod.category] || '#6366f1'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className={`${mod.icon || 'bi bi-grid'}`} style={{ fontSize: 13, color: CAT_COLORS[mod.category] || '#6366f1' }}></i>
                          </div>
                          <div>
                            <div className="fw-semibold" style={{ lineHeight: 1.2 }}>{mod.name}</div>
                            {mod.routePath && <div style={{ fontSize: 11, color: '#94a3b8' }}>{mod.routePath}</div>}
                          </div>
                        </div>
                      </td>
                      <td><code style={{ fontSize: 11, color: '#6366f1', background: '#f5f3ff', padding: '1px 6px', borderRadius: 4 }}>{mod.key}</code></td>
                      <td>
                        <span className="badge" style={{ background: `${CAT_COLORS[mod.category] || '#6366f1'}20`, color: CAT_COLORS[mod.category] || '#6366f1', fontSize: 11, fontWeight: 600 }}>
                          {CAT_LABELS[mod.category] || mod.category}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(mod.industryIds || []).length === 0 ? (
                            <span className="badge bg-secondary" style={{ fontSize: 10 }}>All</span>
                          ) : (mod.industryIds || []).slice(0, 3).map(ind => {
                            const name = typeof ind === 'object' ? ind.name : (industries.find(i => i._id === ind)?.name || '?');
                            return <span key={typeof ind === 'object' ? ind._id : ind} className="badge bg-light text-dark" style={{ fontSize: 10 }}>{name}</span>;
                          })}
                          {(mod.industryIds || []).length > 3 && <span className="badge bg-light text-muted" style={{ fontSize: 10 }}>+{mod.industryIds.length - 3}</span>}
                        </div>
                      </td>
                      <td><span className={`badge bg-${tierColor[mod.minPlanTier] || 'secondary'}`} style={{ fontSize: 10 }}>{mod.minPlanTier}</span></td>
                      <td className="text-center">
                        {mod.isCore
                          ? <i className="bi bi-check-circle-fill text-success"></i>
                          : <i className="bi bi-circle text-muted"></i>}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-primary btn-sm py-0 px-2" onClick={() => openEdit(mod)} title="Edit">
                            <i className="bi bi-pencil" style={{ fontSize: 12 }}></i>
                          </button>
                          <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => handleDelete(mod)} title="Delete">
                            <i className="bi bi-trash" style={{ fontSize: 12 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ───────────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top" style={{ background: '#f8fafc' }}>
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Showing {from}–{to} of {total} modules
                </span>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(1)} disabled={page === 1}>«</button>
                    </li>
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</button>
                    </li>
                    {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                      let p;
                      if (totalPages <= 7) p = i + 1;
                      else if (page <= 4) p = i + 1;
                      else if (page >= totalPages - 3) p = totalPages - 6 + i;
                      else p = page - 3 + i;
                      return (
                        <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</button>
                    </li>
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
